import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw, Loader2 } from "lucide-react";
import api, { API, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

export const siteImageUrl = (path, fallback) => (path ? `${API}/site-assets/${path}` : fallback);

export const SiteImageSetting = ({ slot, settingKey, defaultSrc, defaultLabel, help, aspect = "aspect-[3/4]", width = "w-40" }) => {
    const { settings, refresh } = useSettings();
    const inputRef = useRef(null);
    const [progress, setProgress] = useState(null);
    const [resetting, setResetting] = useState(false);
    const path = settings?.[settingKey];

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) { toast.error("Photo is larger than 8 MB. Please use a smaller file."); return; }
        const fd = new FormData();
        fd.append("file", file);
        setProgress(0);
        try {
            await api.post(`/admin/site-image/${slot}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
            });
            await refresh();
            toast.success("Photo updated");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, "Upload failed"));
        } finally {
            setProgress(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const reset = async () => {
        setResetting(true);
        try {
            await api.delete(`/admin/site-image/${slot}`);
            await refresh();
            toast.success(`Reset to ${defaultLabel.toLowerCase()}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, "Reset failed"));
        } finally {
            setResetting(false);
        }
    };

    return (
        <div className="space-y-3" data-testid={`${slot}-image-setting`}>
            {help && <p className="text-xs text-[#8A6574]">{help}</p>}
            <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className={`${width} ${aspect} border border-[#C29A5B]/50 overflow-hidden bg-[#FDF0EC] shrink-0`}>
                    <img src={siteImageUrl(path, defaultSrc)} alt="Current" className="w-full h-full object-cover" data-testid={`${slot}-preview`} />
                </div>
                <div className="space-y-3">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#7A5262]" data-testid={`${slot}-status`}>{path ? "Custom photo" : defaultLabel}</div>
                    <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${slot}-file-input`} />
                    <div className="flex flex-wrap gap-3">
                        <button type="button" onClick={() => inputRef.current?.click()} disabled={progress !== null} className="btn-gold text-xs" data-testid={`${slot}-upload-btn`}>
                            {progress !== null ? <><Loader2 size={14} className="animate-spin" /> Uploading {progress}%</> : <><Upload size={14} /> Upload new photo</>}
                        </button>
                        {path && (
                            <button type="button" onClick={reset} disabled={resetting} className="btn-outline-gold text-xs" data-testid={`${slot}-reset-btn`}>
                                <RotateCcw size={14} /> {resetting ? "Resetting…" : `Reset to ${defaultLabel.toLowerCase()}`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
